import {
  arrayBufferToBase64,
  generateDPoPKeyPair,
  generateDPoPProof,
  stringToArrayBuffer,
  clearDPoPKey,
  base64ToArrayBuffer,
} from "./webauthn";

type AuthEntity =
  | "xstak"
  | "postex"
  | "callcourier"
  | "postexglobal"
  | "postexsa";

interface AuthSDKConfig {
  apiKey?: string;
  appId?: AuthEntity;
}

const LOCALSTORAGE_TOKEN_KEY = "postex-auth-token";

type AuthStatusType = "no_session" | "session_found" | "webauthn_ready";

interface AuthStatusResponse {
  status: AuthStatusType;
  email?: string;
  webauthn?: boolean;
  challenge?: string;
  credentialIds?: string[];
  rp?: { name: string; host: string };
}

/** Response from POST /auth/initiate */
type InitiateAuthStatusType = "webauthn_challenge" | "otp_sent";

interface InitiateAuthResponse {
  status: InitiateAuthStatusType;
  challenge?: string;
  credentialIds?: string[];
  rp?: { name: string; host: string };
}

interface OTPVerifyResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  verified: boolean;
  email: string;
  [key: string]: any;
}

interface OTPInitiateResponse {
  statusText?: string;
  statusCode?: number;
  success?: boolean;
  message?: string;
  data?: {
    message?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

interface PasskeyRegistrationChallenge {
  challenge: string;
  rp: { name: string; id: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams?: Array<{ type: string; alg: number }>;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  /** Existing credential IDs to exclude (prevents duplicate registration) */
  excludeCredentials?: string[];
}

interface PasskeyRegisterResponse {
  registered: boolean;
  [key: string]: any;
}

interface PasskeyStatusResponse {
  credentialId?: string;
  hasCredentials: boolean;
  [key: string]: any;
}

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  id_token?: string;
  email: string;
  name: string;
  expiresIn?: number;
  tokenType?: string;
  [key: string]: any;
}

/** Response from POST /auth/refresh */
interface RefreshTokenResponse {
  access_token: string;
  id_token?: string;
  token_type: string;
  expires_in: number;
}

const DEFAULT_APP_ID: AuthEntity = "postexglobal";
const AUTH_BASE_URL_BY_APP_ID: Record<AuthEntity, string> = {
  xstak: "https://auth.xstak.com/public/v1",
  postex: "https://auth.postex.pk/public/v1",
  callcourier: "https://auth.callcourier.com.pk/public/v1",
  postexglobal: "https://auth.postexglobal.com/public/v1",
  postexsa: "https://auth.postex.sa/public/v1",
};

export class AuthSDKFetchError extends Error {
  response: { status: number; data?: any };

  constructor(status: number, data?: any, message?: string) {
    super(message ?? `Request failed with status ${status}`);
    this.response = { status, data };
    this.name = "AuthSDKFetchError";
  }
}

const ID_TOKEN_KEY = "auth_sdk_id_token";
const REFRESH_TOKEN_KEY = "auth_sdk_refresh_token";

export class AuthSDK {
  private config: AuthSDKConfig;

  constructor(config: AuthSDKConfig) {
    this.config = config;
  }

  private getBaseUrl(): string {
    const appId = this.config.appId ?? DEFAULT_APP_ID;
    return AUTH_BASE_URL_BY_APP_ID[appId];
  }

  private normalizeAuthIdentifier(
    identifier: string | { email?: string; mobileNumber?: string },
  ): { email?: string; mobileNumber?: string } {
    if (typeof identifier === "string") {
      return { email: identifier };
    }
    return {
      email: identifier.email,
      mobileNumber: identifier.mobileNumber,
    };
  }

  private buildUrl(path: string, params?: Record<string, string>): string {
    const base = this.getBaseUrl().replace(/\/$/, "");
    const pathNorm = path.startsWith("/") ? path : `/${path}`;
    const url = `${base}${pathNorm}`;
    if (!params || Object.keys(params).length === 0) return url;
    const search = new URLSearchParams(params).toString();
    return `${url}?${search}`;
  }

  private async request<T>(
    method: string,
    path: string,
    options?: {
      body?: any;
      params?: Record<string, string>;
      headers?: Record<string, string>;
    },
  ): Promise<{ data: T }> {
    const url = this.buildUrl(path, options?.params);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-API-Key": this.config.apiKey ?? "",
      ...options?.headers,
    };

    const init: RequestInit = {
      method,
      credentials: "include",
      headers,
    };
    if (options?.body !== undefined && options?.body !== null) {
      init.body = JSON.stringify(options.body);
    }

    const response = await fetch(url, init);

    if (!response.ok) {
      let data: any;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : undefined;
      } catch {
        data = undefined;
      }
      if (response.status === 401) {
        await this.clearTokens();
      }
      throw new AuthSDKFetchError(response.status, data);
    }

    const text = await response.text();
    const data = text ? JSON.parse(text) : {};
    return { data };
  }

  /**
   * Returns auth headers (Authorization + DPoP) for the given request.
   * Use this to attach auth to your own HTTP client (e.g. axios request interceptor).
   * @param method - HTTP method (e.g. "GET", "POST")
   * @param url - Full request URL
   */
  async getRequestAuthHeaders(
    method: string,
    url: string,
  ): Promise<Record<string, string>> {
    const token = localStorage.getItem(LOCALSTORAGE_TOKEN_KEY);
    if (!token) return {};
    const fullUrl = url.startsWith("http") ? url : `${this.getBaseUrl()}${url}`;
    const dpopProof = await generateDPoPProof(
      method.toUpperCase(),
      fullUrl,
      token,
    );
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };
    if (dpopProof) headers["DPoP"] = dpopProof;
    return headers;
  }

  /**
   * GET /auth/status - Check if client has trusted device session and what auth method is available.
   * Returns no_session | session_found | webauthn_ready per PostEx Auth BFF spec.
   */
  async getStatus(
    identifier: string | { email?: string; mobileNumber?: string },
  ): Promise<AuthStatusResponse> {
    const params: Record<string, string> = {};
    if (typeof identifier === "string") {
      params.email = identifier;
    } else {
      if (identifier.email) params.email = identifier.email;
      if (identifier.mobileNumber)
        params.mobileNumber = identifier.mobileNumber;
    }
    const response = await this.request<unknown>("GET", "/auth/status", {
      params,
    });
    return (response.data as any).data ?? response.data;
  }

  /**
   * POST /auth/initiate - Unified entry: returns webauthn_challenge or otp_sent.
   * Sets auth_session cookie when otp_sent.
   */
  async initiateAuth(
    identifier: string | { email?: string; mobileNumber?: string },
  ): Promise<InitiateAuthResponse> {
    const body = this.normalizeAuthIdentifier(identifier);
    const response = await this.request<unknown>("POST", "/auth/initiate", {
      body,
    });
    const data = (response.data as any).data ?? response.data;
    return {
      status: data.status,
      challenge: data.challenge,
      credentialIds: data.credentialIds,
      rp: data.rp,
    };
  }

  /**
   * POST /otp/initiate - Direct OTP initiation using email or mobile number.
   * Requires at least one identifier: email or mobileNumber.
   */
  async initiateOTP(
    identifier: string | { email?: string; mobileNumber?: string },
  ): Promise<OTPInitiateResponse> {
    const normalized = this.normalizeAuthIdentifier(identifier);
    const email = normalized.email?.trim();
    const mobileNumber = normalized.mobileNumber?.trim();

    if (!email && !mobileNumber) {
      throw new Error("Either mobileNumber or email is required");
    }

    const body: { email?: string; mobileNumber?: string } = {};
    if (email) body.email = email;
    if (mobileNumber) body.mobileNumber = mobileNumber;

    const response = await this.request<unknown>("POST", "/otp/initiate", {
      body,
    });
    const data = response.data as any as OTPInitiateResponse;
    return {
      message: data.message ?? data.data?.message,
    };
  }

  /**
   * POST /otp/verify - Verifies the OTP code entered by the user.
   * Stores tokens from the response.
   */
  async verifyOTP(otp: string): Promise<OTPVerifyResponse> {
    await generateDPoPKeyPair();
    const dpopProof = await generateDPoPProof(
      "POST",
      `${this.getBaseUrl()}/otp/verify`,
    );

    const response = await this.request<unknown>("POST", "/otp/verify", {
      body: { otp },
      headers: dpopProof ? { DPoP: dpopProof } : {},
    });
    const data = (response.data as any).data ?? response.data;

    const result = data.AuthenticationResult ?? data;
    const accessToken =
      result.access_token ?? result.accessToken ?? result.AccessToken;
    const refreshToken =
      result.refresh_token ?? result.refreshToken ?? result.RefreshToken;
    const idToken = result.id_token ?? result.idToken ?? result.IdToken;
    const expiresIn =
      result.expires_in ?? result.expiresIn ?? result.ExpiresIn ?? 3600;
    const tokenType =
      result.token_type ?? result.tokenType ?? result.TokenType ?? "Bearer";

    if (accessToken) {
      await this.storeTokens({
        accessToken,
        refreshToken: refreshToken ?? "",
        idToken: idToken ?? "",
        expiresIn: expiresIn ?? 3600,
        tokenType: tokenType ?? "Bearer",
      });
    }

    return {
      access_token: accessToken,
      refresh_token: refreshToken ?? "",
      id_token: idToken ?? "",
      expires_in: expiresIn,
      token_type: tokenType,
      verified: result.verified ?? result.Verified ?? true,
      email: result.email ?? result.Email ?? "",
      ...result,
    };
  }

  /**
   * POST /resend - Resend OTP code to the user's email.
   * No payload required, relies on auth_session cookie.
   */
  async resendOTP(): Promise<{ success: boolean; message?: string }> {
    const response = await this.request<unknown>("POST", "/otp/resend", {});
    const data = (response.data as any).data ?? response.data;

    return {
      success: data.success ?? true,
      message: data.message,
      ...data,
    };
  }

  /**
   * POST /otp/signup/verify - Verify OTP for signup flow.
   * Stores tokens from the response when provided by backend.
   */
  async verifySignupOTP({
    mobileNumber,
    otp,
  }: {
    mobileNumber: string;
    otp: string;
  }): Promise<OTPVerifyResponse> {
    await generateDPoPKeyPair();
    const dpopProof = await generateDPoPProof(
      "POST",
      `${this.getBaseUrl()}/otp/signup/verify`,
    );

    const response = await this.request<unknown>("POST", "/otp/signup/verify", {
      body: { mobileNumber, otp },
      headers: dpopProof ? { DPoP: dpopProof } : {},
    });
    const data = (response.data as any).data ?? response.data;

    const result = data.AuthenticationResult ?? data;
    const accessToken =
      result.access_token ?? result.accessToken ?? result.AccessToken;
    const refreshToken =
      result.refresh_token ?? result.refreshToken ?? result.RefreshToken;
    const idToken = result.id_token ?? result.idToken ?? result.IdToken;
    const expiresIn =
      result.expires_in ?? result.expiresIn ?? result.ExpiresIn ?? 3600;
    const tokenType =
      result.token_type ?? result.tokenType ?? result.TokenType ?? "Bearer";

    if (accessToken) {
      await this.storeTokens({
        accessToken,
        refreshToken: refreshToken ?? "",
        idToken: idToken ?? "",
        expiresIn: expiresIn ?? 3600,
        tokenType: tokenType ?? "Bearer",
      });
    }

    return {
      access_token: accessToken,
      refresh_token: refreshToken ?? "",
      id_token: idToken ?? "",
      expires_in: expiresIn,
      token_type: tokenType,
      verified: result.verified ?? result.Verified ?? true,
      email: result.email ?? result.Email ?? "",
      ...result,
    };
  }

  /**
   * POST /otp/signup/resend - Resend OTP in signup flow.
   */
  async resendSignupOTP({
    mobileNumber,
    email = "",
  }: {
    mobileNumber: string;
    email?: string;
  }): Promise<{ success: boolean; message?: string }> {
    const response = await this.request<unknown>("POST", "/otp/signup/resend", {
      body: { email, mobileNumber },
    });
    const data = (response.data as any).data ?? response.data;

    return {
      success: data.success ?? true,
      message: data.message,
      ...data,
    };
  }

  /**
   * GET /magic-link - Verify a magic link token.
   * Sets auth_session cookie on success.
   */
  async verifyMagicLink(token: string, email: string): Promise<void> {
    await this.request("GET", "/verify/magic-link", {
      params: { token, email, redirect_mode: "frontend" },
    });
  }

  /**
   * POST /magic-link/complete - Complete magic link authentication.
   * Requires auth_session cookie set by verifyMagicLink.
   * Stores tokens from the response.
   */
  async completeMagicLink(): Promise<OTPVerifyResponse> {
    const response = await this.request<unknown>(
      "POST",
      "/verify/magic-link/complete",
      {
        body: {},
      },
    );
    const data = (response.data as any).data ?? response.data;

    const result = data.AuthenticationResult ?? data;
    const accessToken =
      result.access_token ?? result.accessToken ?? result.AccessToken;
    const refreshToken =
      result.refresh_token ?? result.refreshToken ?? result.RefreshToken;
    const idToken = result.id_token ?? result.idToken ?? result.IdToken;
    const expiresIn =
      result.expires_in ?? result.expiresIn ?? result.ExpiresIn ?? 3600;
    const tokenType =
      result.token_type ?? result.tokenType ?? result.TokenType ?? "Bearer";

    if (accessToken) {
      await this.storeTokens({
        accessToken,
        refreshToken: refreshToken ?? "",
        idToken: idToken ?? "",
        expiresIn: expiresIn ?? 3600,
        tokenType: tokenType ?? "Bearer",
      });
    }

    return {
      access_token: accessToken,
      refresh_token: refreshToken ?? "",
      id_token: idToken ?? "",
      expires_in: expiresIn,
      token_type: tokenType,
      verified: result.verified ?? result.Verified ?? true,
      email: result.email ?? result.Email ?? "",
      ...result,
    };
  }

  async initiatePasskeyRegistration(): Promise<PasskeyRegistrationChallenge> {
    const response = await this.request<unknown>(
      "POST",
      "/webauthn/initiate/challenge",
      {
        body: {},
      },
    );
    return (response.data as any).data ?? response.data;
  }

  async registerPasskey(email: string): Promise<PasskeyRegisterResponse> {
    const challenge = await this.initiatePasskeyRegistration();
    let userIdBuffer: ArrayBuffer;
    if (challenge?.user?.id) {
      try {
        userIdBuffer = base64ToArrayBuffer(challenge.user.id);
      } catch {
        userIdBuffer = stringToArrayBuffer(challenge.user.id);
      }
    } else {
      userIdBuffer = stringToArrayBuffer(email);
    }

    const pubKeyCredParams = (
      challenge.pubKeyCredParams ?? [
        { type: "public-key", alg: -7 },
        { type: "public-key", alg: -257 },
      ]
    ).map((param) => ({
      type: "public-key" as const,
      alg: param.alg,
    }));

    const excludeCredentials: PublicKeyCredentialDescriptor[] | undefined =
      challenge.excludeCredentials?.map((id) => ({
        type: "public-key" as const,
        id: base64ToArrayBuffer(id),
      }));

    const createOptions: PublicKeyCredentialCreationOptions = {
      challenge: base64ToArrayBuffer(challenge.challenge),
      rp: {
        name: challenge.rp?.name ?? "XPay",
        id: challenge.rp?.id ?? window.location.hostname,
      },
      user: {
        id: userIdBuffer,
        name: challenge.user?.name ?? email,
        displayName: challenge.user?.displayName ?? email,
      },
      pubKeyCredParams,
      excludeCredentials,
      timeout: challenge.timeout ?? 60000,
      attestation: "direct",
      authenticatorSelection: {
        residentKey: "required",
        userVerification: "required",
        authenticatorAttachment: "platform",
      },
    };

    const credential = (await navigator.credentials.create({
      publicKey: createOptions,
    })) as PublicKeyCredential;

    if (!credential) throw new Error("Credential creation failed");
    const attResp = credential.response as AuthenticatorAttestationResponse;
    const payload = {
      clientDataJSON: arrayBufferToBase64(attResp.clientDataJSON),
      attestationObject: arrayBufferToBase64(attResp.attestationObject),
      rawId: arrayBufferToBase64(credential.rawId as ArrayBuffer),
    };
    if (!payload.rawId) throw new Error("Raw ID is required");
    await generateDPoPKeyPair();
    const dpopProof = await generateDPoPProof(
      "POST",
      `${this.getBaseUrl()}/webauthn/register/challenge`,
    );

    const response = await this.request<unknown>(
      "POST",
      "/webauthn/register/challenge",
      {
        body: payload,
        headers: dpopProof ? { DPoP: dpopProof } : {},
      },
    );

    return (response.data as any).data ?? response.data;
  }

  /**
   * POST /webauthn/authenticate/challenge - Authenticate with passkey.
   */
  async authenticateWithPasskey({
    challenge,
    rp,
    credentialIds,
  }: {
    challenge: string;
    rp: { name: string; host: string };
    credentialIds: string[];
  }): Promise<AuthResponse> {
    const assertion = (await navigator.credentials.get({
      publicKey: {
        challenge: base64ToArrayBuffer(challenge),
        rpId: rp?.host ?? undefined,
        allowCredentials: credentialIds.map((id: string) => ({
          type: "public-key" as const,
          id: base64ToArrayBuffer(id),
          transports: ["internal"] as AuthenticatorTransport[],
        })),
        timeout: 60000,
        userVerification: "required",
      },
    })) as PublicKeyCredential;

    if (!assertion) throw new Error("Authentication failed");
    const assertionResponse =
      assertion.response as AuthenticatorAssertionResponse;
    const payload = {
      clientDataJSON: arrayBufferToBase64(assertionResponse.clientDataJSON),
      authenticatorData: arrayBufferToBase64(
        assertionResponse.authenticatorData,
      ),
      signature: arrayBufferToBase64(assertionResponse.signature),
      rawId: arrayBufferToBase64(assertion.rawId as ArrayBuffer),
      userHandle: assertionResponse.userHandle
        ? arrayBufferToBase64(assertionResponse.userHandle)
        : "",
    };

    await generateDPoPKeyPair();
    const dpopProof = await generateDPoPProof(
      "POST",
      `${this.getBaseUrl()}/webauthn/authenticate/challenge`,
    );

    const response = await this.request<unknown>(
      "POST",
      "/webauthn/authenticate/challenge",
      {
        body: payload,
        headers: dpopProof ? { DPoP: dpopProof } : {},
      },
    );

    const data = (response.data as any).data ?? response.data;
    const result = data.AuthenticationResult ?? data;
    const accessToken =
      result.AccessToken ?? result.accessToken ?? result.access_token;
    const refreshToken =
      result.RefreshToken ?? result.refreshToken ?? result.refresh_token;
    const idToken = result.IdToken ?? result.idToken ?? result.id_token;

    if (accessToken) {
      await this.storeTokens({
        accessToken,
        refreshToken: refreshToken ?? "",
        idToken: idToken ?? "",
        expiresIn: result.expiresIn ?? result.ExpiresIn ?? 3600,
        tokenType: result.tokenType ?? result.token_type ?? "Bearer",
      });
    }

    return {
      access_token: accessToken,
      refresh_token: refreshToken ?? "",
      id_token: idToken,
      email: result.email,
      name: result.name ?? result.userName,
      expiresIn: result.expiresIn ?? result.ExpiresIn,
      tokenType: result.tokenType ?? result.token_type,
      ...result,
    };
  }
  /**
   * GET /webauthn/credentials/:username - Get user's passkey status.
   */
  async getPasskeyStatus(username: string): Promise<PasskeyStatusResponse> {
    const encoded = encodeURIComponent(username);
    const path = `/webauthn/credentials/${encoded}`;
    const signed = await this.signRequest("GET", path);
    const response = await this.request<unknown>("GET", path, {
      headers: signed.headers as Record<string, string>,
    });
    const data = (response.data as any).data ?? response.data;
    return {
      ...data,
      hasCredentials: Boolean(data.hasCredentials ?? data.credentialId),
    };
  }

  /**
   * DELETE /webauthn/credentials/:username - Remove passkey.
   */
  async removePasskey(username: string): Promise<void> {
    const encoded = encodeURIComponent(username);
    const path = `/webauthn/credentials/${encoded}`;
    const signed = await this.signRequest("DELETE", path);
    await this.request("DELETE", path, {
      headers: signed.headers as Record<string, string>,
    });
  }

  /**
   * Signs a request with DPoP and Authorization headers (internal use).
   */
  async signRequest(
    method: string,
    url: string,
    config: any = {},
  ): Promise<any> {
    const fullUrl = url.startsWith("http") ? url : `${this.getBaseUrl()}${url}`;
    const authHeaders = await this.getRequestAuthHeaders(method, fullUrl);
    return { ...config, headers: { ...config.headers, ...authHeaders } };
  }

  /**
   * Replaces native fetch or Axios with a DPoP-signed version.
   */
  async authenticatedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;
    const method = init?.method || "GET";

    const signedInit = await this.signRequest(method, url, {
      headers: init?.headers,
    });
    return fetch(input, { ...init, headers: signedInit.headers });
  }

  /**
   * Store tokens from /webauthn/authenticate (per spec: sessionStorage preferred).
   */
  async storeTokens(tokens: {
    accessToken: string;
    refreshToken?: string;
    idToken?: string;
    expiresIn?: number;
    tokenType?: string;
  }): Promise<void> {
    localStorage.setItem(LOCALSTORAGE_TOKEN_KEY, tokens.accessToken);
    if (tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    }
    if (tokens.idToken) {
      localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
    }
  }

  /**
   * Clear stored tokens (call on logout).
   */
  async clearTokens(): Promise<void> {
    localStorage.removeItem(LOCALSTORAGE_TOKEN_KEY);
    localStorage.removeItem(ID_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  /**
   * POST /auth/refresh - Refresh access token using server-stored refresh token.
   * Requires trusted device cookie (td). Refresh token is stored server-side.
   * Rate limit: 10 requests per minute.
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    const response = await this.request<unknown>("POST", "/auth/refresh", {
      body: {},
    });
    const data = (response.data as any).data ?? response.data;

    const accessToken = data.access_token;
    const idToken = data.id_token ?? "";
    const tokenType = data.token_type ?? "Bearer";
    const expiresIn = data.expires_in ?? 3600;

    if (accessToken) {
      await this.storeTokens({
        accessToken,
        idToken,
        expiresIn,
        tokenType,
      });
    }

    return {
      access_token: accessToken,
      id_token: idToken,
      token_type: tokenType,
      expires_in: expiresIn,
    };
  }

  /**
   * POST /auth/logout - Logout from the current device.
   * Revokes the current token and trusted device, then clears local tokens.
   */
  async logout(): Promise<void> {
    try {
      const token = await this.getAccessToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      await this.request("POST", "/auth/logout", { body: {}, headers });
      clearDPoPKey();
    } catch {
      // Silently fail - proceed with local cleanup
    }
    await this.clearTokens();
  }

  async getAccessToken(): Promise<string | null> {
    return localStorage.getItem(LOCALSTORAGE_TOKEN_KEY);
  }
}

if (typeof window !== "undefined") {
  (window as any).AuthSDK = AuthSDK;
}

const globalObj = (
  typeof window !== "undefined" ? window : globalThis
) as Record<string, unknown>;
globalObj.AuthSDK = AuthSDK;